/** Les collections et le prestige, en volets séparés. */
import type { Ctx } from "../contexte.ts"
import { sousOnglets } from "../contexte.ts"
import { vueClassement } from "./classement.ts"
import {
  catalogue, obtenus, avancement as ouEnEst, points, pointsDe, libelleCategorie, totalPoints,
  type Categorie, type Succes,
} from "../../sim/succes.ts"
import { esc, nb, jauge, orJauge } from "../fmt.ts"
import { icone, ic } from "../sprites.ts"
import { item, monstre } from "../../sim/contenu.ts"
import { ELEMENTS, resDe } from "../../sim/combat.ts"
import { avancement } from "../../sim/collection.ts"
import { offrandeDuJour, almanaxActif, jourDe, BONUS_ALMANAX } from "../../sim/almanax.ts"
import { TOUS_DOFUS, effetsDofus } from "../../sim/dofus.ts"

export const SOUS_COLLEC: [string, string][] = [
  ["succes", "Succès"], ["classement", "Classement"], ["dofus", "Dofus"], ["archi", "Archimonstres"],
  ["recherche", "Recherchés"], ["records", "Records"], ["almanax", "Almanax"],
]

export function vueCollection(c: Ctx): string {
  const sous = SOUS_COLLEC.some(([k]) => k === c.sous) ? c.sous : "succes"
  return `${sousOnglets(sous, SOUS_COLLEC)}${
    sous === "succes" ? vueSucces(c)
    : sous === "classement" ? vueClassement(c, c.classement)
    : sous === "archi" ? galerie(c, "archimonstres", c.etat.archimonstres, "archimonstre")
    : sous === "recherche" ? galerie(c, "recherches", c.etat.recherches, "recherche")
    : sous === "records" ? records(c)
    : sous === "almanax" ? almanax(c)
    : dofus(c)}`
}

/**
 * Les succès (GDD §12).
 *
 * Le catalogue compte plus de trois cents entrées — une par famille de
 * monstres, une par zone, trois par métier — et un mur pareil ne se parcourt
 * pas : les catégories sont **repliées**, une recherche traverse tout, et un
 * filtre sépare ce qui reste à faire de ce qui est pris.
 *
 * Ce qu'on affiche vient de `obtenus`, qui réunit l'acquis **inscrit dans la
 * partie** et ce que l'état remplit à l'instant. Un succès ne se reperd donc
 * jamais : dépenser ses kamas ne retire pas « posséder cent mille kamas ».
 */
function vueSucces(c: Ctx): string {
  const { etat, contenu } = c
  const tous = catalogue(contenu)
  const acquis = new Set(obtenus(etat, contenu))
  const gagnes = points([...acquis], contenu)
  const total = totalPoints(contenu)

  const q = c.rechercheSucces.trim().toLowerCase()
  const filtre = c.filtreSucces
  const retenu = tous.filter((s) => {
    if (filtre === "faits" && !acquis.has(s.cle)) return false
    if (filtre === "restants" && acquis.has(s.cle)) return false
    if (!q) return true
    return s.nom.toLowerCase().includes(q) || s.detail.toLowerCase().includes(q)
  })

  const parCategorie = new Map<Categorie, Succes[]>()
  for (const s of retenu) parCategorie.set(s.categorie, [...(parCategorie.get(s.categorie) ?? []), s])
  // Le compte affiché porte sur la **catégorie entière**, jamais sur ce que le
  // filtre laisse passer : sous « à faire », chaque catégorie annonçait « 0 / 5 ».
  const totauxCat = new Map<Categorie, { pris: number; total: number }>()
  for (const s of tous) {
    const v = totauxCat.get(s.categorie) ?? { pris: 0, total: 0 }
    v.total++
    if (acquis.has(s.cle)) v.pris++
    totauxCat.set(s.categorie, v)
  }

  return `
  <section class="carte">
    <h2>Succès <em>${acquis.size} / ${tous.length}</em></h2>
    ${jauge(acquis.size / tous.length, orJauge)}
    <p class="aide">${nb(gagnes)} points sur ${nb(total)}. Un palier élevé vaut
    plus qu'un palier facile, et un contenu de haut niveau plus qu'un contenu de
    départ : le classement récompense l'exploit, pas le remplissage.</p>
    <input class="recherche" id="rechercheSucces" type="search" placeholder="Chercher un succès…"
           value="${esc(c.rechercheSucces)}" autocomplete="off" />
    <nav class="filtres">
      ${([["tout", "Tout"], ["restants", "À faire"], ["faits", "Obtenus"]] as const).map(([k, lib]) =>
        `<button data-filtresucces="${k}" aria-current="${filtre === k}">${lib}</button>`).join("")}
    </nav>
  </section>

  ${retenu.length === 0 ? `<section class="carte"><p class="aide">Aucun succès ne correspond.</p></section>` : ""}

  ${[...parCategorie].map(([cat, liste]) => {
    const compte = totauxCat.get(cat) ?? { pris: 0, total: liste.length }
    const ouverte = c.categorieSucces === cat || Boolean(q)
    // À faire d'abord, du plus proche du but ; l'acquis se range à la fin.
    const ordre = [...liste].sort((a, b) => {
      const fa = Number(acquis.has(a.cle)), fb = Number(acquis.has(b.cle))
      return fa - fb || ouEnEst(b, etat, contenu) - ouEnEst(a, etat, contenu) || a.seuil - b.seuil
    })
    return `<section class="carte">
      <button class="replier" data-catsucces="${cat}" aria-expanded="${ouverte}">
        <span class="nom">${esc(libelleCategorie(cat))}</span>
        <span class="compte">${compte.pris} / ${compte.total}${
          liste.length !== compte.total ? ` <em>· ${liste.length} affiché${liste.length > 1 ? "s" : ""}</em>` : ""}</span>
        <span class="chevron">${ouverte ? "▴" : "▾"}</span>
      </button>
      ${!ouverte ? "" : `<div class="succes">${ordre.slice(0, 200).map((s) => {
        const fait = acquis.has(s.cle)
        const valeur = s.mesure(etat, contenu)
        return `<div class="succ ${fait ? "fait" : ""}">
          <span class="marque">${fait ? "★" : "☆"}</span>
          <div class="txt">
            <span class="nom">${esc(s.nom)}</span>
            <span class="det">${esc(s.detail)}</span>
            ${fait ? "" : jauge(ouEnEst(s, etat, contenu), orJauge)}
          </div>
          <span class="pts">${fait
            ? `+${pointsDe(s)}`
            : `${nb(Math.floor(valeur))} / ${nb(s.seuil)}`}</span>
        </div>`
      }).join("")}${ordre.length > 200
        ? `<p class="aide">et ${ordre.length - 200} autres — affine ta recherche.</p>` : ""}</div>`}
    </section>`
  }).join("")}`
}

function dofus(c: Ctx): string {
  const eff = effetsDofus(c.etat)
  const bonus = [
    eff.degats > 1 ? `dégâts ×${eff.degats.toFixed(2)}` : "",
    eff.recolte > 1 ? `récolte ×${eff.recolte.toFixed(2)}` : "",
    eff.vitesse > 1 ? `cadence ×${eff.vitesse.toFixed(2)}` : "",
    eff.regen > 1 ? `régénération ×${eff.regen.toFixed(2)}` : "",
    eff.prospection > 0 ? `prospection +${eff.prospection}` : "",
    eff.doubleJet ? "double jet de craft" : "",
  ].filter(Boolean)

  return `<section class="carte">
    <h2>Les Dofus <em>${c.etat.dofus.length} / ${TOUS_DOFUS.length}</em></h2>
    <p class="aide">Chacun met à l'épreuve un système différent du jeu. Leurs effets valent pour tout le compte.</p>
    <div class="dofus">${TOUS_DOFUS.map((d) => {
      const pr = d.progres(c.etat, c.contenu)
      const eu = c.etat.dofus.includes(d.id)
      const ico = icone("items", d.item, 44)
      return `<div class="oeuf ${eu ? "obtenu" : ""}">
        <span class="oeufIc" style="${ico || `background:${d.couleur};border-radius:50% / 60% 60% 40% 40%`}"></span>
        <div class="txt">
          <span class="nom">${esc(d.nom)}</span>
          <span class="cond">${esc(d.condition)}</span>
          ${jauge(pr.actuel / pr.requis, `linear-gradient(${d.couleur},${d.couleur})`)}
        </div>
        <b>${eu ? "✓" : `${nb(pr.actuel)}/${nb(pr.requis)}`}</b>
      </div>`
    }).join("")}</div>
    ${bonus.length ? `<p class="verdict ok">Bonus de compte : ${bonus.join(" · ")}.</p>` : ""}
  </section>`
}

/**
 * La fiche d'une créature de collection.
 *
 * Les vignettes n'étaient que des images avec une infobulle : on voyait des
 * silhouettes sans savoir ce qu'elles valaient ni où les trouver. Un clic
 * ouvre maintenant l'entrée de bestiaire — c'est aussi ce qui dit **par quel
 * élément l'attaquer**, la donnée la plus utile d'une collection à chasser.
 *
 * Le nom est montré même sur ce qu'on n'a pas encore : une collection sert à
 * savoir quoi poursuivre, pas à cacher la liste.
 */
function ficheMonstre(c: Ctx, captures: number[]): string {
  if (c.monstreChoisi === null) return ""
  const m = monstre(c.contenu, c.monstreChoisi)
  if (!m) return ""
  const pal = m.paliers[0]
  if (!pal) return ""
  const eu = captures.includes(m.id)
  // La fiche affiche la résistance **effective**, celle que le combat applique :
  // certains boss portent 200 ou 900 dans les données de Dofus, et l'annoncer
  // tel quel promettrait une invincibilité qui n'existe plus (voir PLAFOND_RES).
  const r = (e: (typeof ELEMENTS)[number]) => resDe(pal, e)
  const faible = ELEMENTS.reduce((a, e) => (r(e) < r(a) ? e : a), ELEMENTS[0])

  const butin = (m.butin ?? [])
    .map((b) => ({ b, it: item(c.contenu, b.item) }))
    .filter((x) => x.it)
    .sort((a, b) => (b.b.taux[0] ?? 0) - (a.b.taux[0] ?? 0))
    .slice(0, 12)

  return `<div class="fiche-mob">
    <div class="tete">
      <span class="portrait" style="${icone("monstres", m.id, 56)}"></span>
      <div class="txt">
        <span class="nom">${esc(m.nom)}</span>
        <span class="det">niveau ${pal.niveau} · ${nb(pal.pv)} points de vie${eu ? "" : " · pas encore pris"}</span>
      </div>
      <button class="fermer" data-fermermob="1" title="Fermer">✕</button>
    </div>

    <p class="titrelot">Résistances</p>
    <div class="resis">${ELEMENTS.map((e) => `
      <span class="r ${r(e) < 0 ? "faible" : r(e) >= 25 ? "dure" : ""} ${e === faible ? "meilleur" : ""}"
        title="${r(e) < 0 ? "Faiblesse" : "Résistance"} ${e} : ${r(e)} %">
        ${ic(e, 14)}<b>${r(e) > 0 ? "+" : ""}${r(e)}</b>
      </span>`).join("")}</div>
    <p class="aide">Frappe-le en <b>${esc(faible)}</b> : c'est là qu'il se défend le moins.</p>

    ${butin.length === 0 ? `<p class="aide">Il ne laisse rien.</p>` : `
      <p class="titrelot">Ce qu'il laisse</p>
      <div class="butinmob">${butin.map(({ b, it }) => `
        <span class="bl">
          <span class="ic" style="${icone("items", it!.id, 24)}"></span>
          <span class="nm">${esc(it!.nom)}</span>
          <b>${(b.taux[0] ?? 0).toFixed(1)} %</b>
        </span>`).join("")}</div>`}
  </div>`
}

function galerie(c: Ctx, cle: "archimonstres" | "recherches", captures: number[], icn: string): string {
  const table = c.contenu[cle] as Record<string, { id: number }>
  const a = avancement(table, captures)
  // Tout est montré. La galerie s'arrêtait à 36 vignettes et renvoyait le reste
  // à un « et 270 autres à découvrir » : sur un écran de collection, c'est
  // précisément la liste complète qu'on vient voir.
  const parZone = new Map<number | null, typeof table[string][]>()
  for (const x of Object.values(table)) {
    const z = (x as { zone?: number | null }).zone ?? null
    parZone.set(z, [...(parZone.get(z) ?? []), x])
  }
  const zones = [...parZone].sort((a, b) => {
    const na = c.contenu.zones[String(a[0])]?.niveauMin ?? 999
    const nb2 = c.contenu.zones[String(b[0])]?.niveauMin ?? 999
    return na - nb2
  })

  const texte = cle === "archimonstres"
    ? `Ils surgissent pendant le farm et ne restent que trois minutes : il faut être là.${c.etat.archiRates > 0 ? ` <b>${c.etat.archiRates} manqué${c.etat.archiRates > 1 ? "s" : ""}.</b>` : ""}`
    : `0,05 % par kill, aucune présence requise. Ils sont répartis sur toutes les tranches de niveau : les compléter demande de retourner farmer les zones basses.`

  return `<section class="carte">
    <h2>${ic(icn, 22)}${cle === "archimonstres" ? "Archimonstres" : "Avis de recherche"} <em>${a.pris} / ${a.total}</em></h2>
    ${jauge(a.pct / 100, orJauge)}
    <p class="aide">${texte}</p>
    ${ficheMonstre(c, captures)}
    ${zones.map(([z, liste]) => {
      const nom = z != null ? c.contenu.zones[String(z)]?.nom ?? "Ailleurs" : "Ailleurs"
      const n = liste.filter((x) => captures.includes(x.id)).length
      return `<div class="lotzone">
        <p class="titrelot">${esc(nom)} <em>${n} / ${liste.length}</em></p>
        <div class="collec">${liste.map((x) => {
          const eu = captures.includes(x.id)
          const m = monstre(c.contenu, x.id)
          return `<button class="vign ${eu ? "" : "vide"} ${c.monstreChoisi === x.id ? "choisi" : ""}"
            data-monstre="${x.id}" title="${esc(m?.nom ?? "")}"
            style="${icone("monstres", x.id, 44)}"></button>`
        }).join("")}</div>
      </div>`
    }).join("")}
  </section>`
}

function records(c: Ctx): string {
  const rec = Object.entries(c.etat.recordsCraft)
  const moy = rec.length ? Math.round(rec.reduce((a, [, v]) => a + v, 0) / rec.length) : 0
  return `<section class="carte">
    <h2>Records de craft <em>${rec.length} objets · ${moy} % moyen</em></h2>
    <p class="aide">Le meilleur jet obtenu sur chaque objet, conservé même si tu l'as refusé.</p>
    ${rec.length === 0 ? `<p class="aide">Aucun craft pour l'instant.</p>` : `
      <div class="sac">${rec.sort((a, b) => b[1] - a[1]).slice(0, 40).map(([id, pct]) => `
        <div class="ligne">
          <span class="mini" style="${icone("items", Number(id), 32)}"></span>
          <span class="txt"><span class="nom">${esc(item(c.contenu, Number(id))?.nom ?? "?")}</span></span>
          <b class="${pct === 100 ? "parfait" : ""}">${pct} %</b>
        </div>`).join("")}</div>`}
  </section>`
}

function almanax(c: Ctx): string {
  const o = offrandeDuJour(c.contenu, Date.now())
  const oi = o ? item(c.contenu, o.item) : null
  const dispo = o ? c.etat.inventaire[o.item] ?? 0 : 0
  const fait = c.etat.almanaxJour === jourDe(Date.now())
  const actif = almanaxActif(c.etat.almanaxJusqua, Date.now())
  return `<section class="carte">
    <h2>Almanax <em>offrande du jour</em></h2>
    ${!o || !oi ? `<p class="aide">Aucune offrande disponible.</p>` : `
      <div class="offrande">
        <span class="ic" style="${icone("items", oi.id, 64)}"></span>
        <div class="txt">
          <b>${o.qte} × ${esc(oi.nom)}</b>
          <span class="${dispo >= o.qte ? "ok" : "ko"}">${nb(dispo)} en stock</span>
        </div>
      </div>
      ${actif ? `<p class="verdict ok">Bonus actif : +${BONUS_ALMANAX} % d'XP, au combat comme aux métiers.</p>` : ""}
      ${fait ? `<p class="aide">Offrande déposée. Reviens demain.</p>`
             : `<button class="${dispo >= o.qte ? "primaire" : ""}" id="offrir" ${dispo >= o.qte ? "" : "disabled"}>Offrir · +${BONUS_ALMANAX} % d'XP pendant 24 h</button>`}
      <p class="aide">Le rendez-vous quotidien : il oblige à garder un fond de stock plutôt que tout vendre ou tout crafter.</p>`}
  </section>`
}
